import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import { usePermissions } from '../../hooks/usePermissions';
import { useTheme } from '@fti/design-system';
import type { BackupStatus } from '../../api/dastarkhan';

interface BackupWatermarkProps {
  projectId: string;
}

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export const BackupWatermark: React.FC<BackupWatermarkProps> = ({ projectId }) => {
  const { isAdmin } = usePermissions();
  const { theme } = useTheme();
  const { fetchData } = useAuthenticatedFetch();
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  const [status, setStatus] = useState<BackupStatus | null>(null);

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;

    const doFetch = async () => {
      try {
        const result = await fetchRef.current(
          `/dastarkhan/projects/${projectId}/backup-status`
        ) as BackupStatus | undefined;
        if (!cancelled && result) setStatus(result);
      } catch {
        // Silently fail — watermark is non-critical
      }
    };

    doFetch();
    const interval = setInterval(doFetch, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAdmin, projectId]);

  if (!isAdmin || !status) return null;

  const isFailed = status.last_backup_status === 'failed';
  const hasBackup = status.last_backup_at != null;

  let text: string;
  let color: string;

  if (isFailed) {
    text = 'Last backup failed \u2014 contact tyler.rendina@fticonsulting.com';
    color = theme.colors.semantic.error.default;
  } else if (hasBackup) {
    text = `Last backup: ${formatRelativeTime(status.last_backup_at!)}`;
    color = theme.colors.text.secondary;
  } else {
    text = 'No backups yet';
    color = theme.colors.text.secondary;
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 8,
      left: 8,
      zIndex: 50,
      fontSize: '0.7rem',
      color,
      opacity: 0.8,
      pointerEvents: 'none',
      userSelect: 'none',
    }}>
      {text}
    </div>
  );
};
